import { createSlice } from "@reduxjs/toolkit";
import authApi from "interceptor/authInterceptor";
import { cloneDeep } from "lodash";
import { dispatch } from "store";
import { handleApiCall } from "utils/apiCallHandler";
import { dateFormat } from "utils/date";
import { decryptBankingData, formatCurrency } from "utils/helper";

export type lenderSettingsProps = {
  isLoading: boolean;
  error: null;
  superLenders: any;
  selectedLender: any;
  lenderAccount: any;
  action: string;
  isProduction: boolean;
};

const initialState: lenderSettingsProps = {
  isLoading: false,
  error: null,
  superLenders: [],
  selectedLender: null,
  lenderAccount: null,
  action: "",
  isProduction: false,
};

const formateLender = (lender: any) => {
  lender.start_date = dateFormat(lender.start_date);
  lender.live_receivables_account_number = decryptBankingData(
    lender.live_receivables_account_number
  );
  lender.live_receivables_account_bsb = decryptBankingData(
    lender.live_receivables_account_bsb
  );
  lender.test_receivables_account_number = decryptBankingData(
    lender.test_receivables_account_number
  );
  lender.test_receivables_account_bsb = decryptBankingData(
    lender.test_receivables_account_bsb
  );
  lender.total_balance = formatCurrency(
    lender.total_amount - lender.total_deduction
  );
  lender.total_amount = formatCurrency(lender.total_amount);
  lender.total_deduction = formatCurrency(lender.total_deduction);
  lender.interest_rate_value = formatCurrency(
    Number(lender.interest_rate_value)
  );

  return lender;
};

// ==============================|| SLICE - Email Settings Tab ||============================== //
const lenderSettings = createSlice({
  name: "lenderSettings",
  initialState,
  reducers: {
    setLenderSettings(state, action) {
      state.superLenders = action.payload.lenders || initialState.superLenders;
      state.isProduction =
        action.payload.isProduction || initialState.isProduction;
      state.selectedLender = null;
      state.action = "";
    },
    setSelectedLender(state, action) {
      const { actionType, id } = action.payload;
      const clonedSuperLender = cloneDeep(state.superLenders);
      const superLender =
        clonedSuperLender.find(({ _id }: { _id: string }) => _id === id) ||
        initialState.selectedLender;
      state.selectedLender = superLender || null;
      state.action = actionType;
    },
    setLenderAccount(state, action) {
      if (!action.payload) {
        state.lenderAccount = initialState.lenderAccount;
        return;
      }
      const { id, account_name, account_number, api_key, isProduction, live_fee_account_number } = action.payload;
      const environment = isProduction ? "live" : "test";
      const payload = {
        id,
        lender_name: account_name,
        [`${environment}_api_key`]: api_key,
        [`${environment}_account_number`]: account_number,
        ...(isProduction && {live_fee_account_number})
      };
      state.isProduction = isProduction || initialState.isProduction;
      state.lenderAccount = payload || initialState.lenderAccount;
    },
    setLoadingStatus(state, action) {
      state.isLoading = action.payload;
    },
    hasError(state, action) {
      state.error = action.payload;
    },
    clearLenderSettings: () => initialState,
  },
});

export default lenderSettings.reducer;

export const {
  setLenderSettings,
  setSelectedLender,
  setLenderAccount,
  setLoadingStatus,
  hasError,
  clearLenderSettings,
} = lenderSettings.actions;

export function getLenderAccountList(companyId: string, providerType: string) {
  return async () => {
    return handleApiCall(
      () =>
        authApi.get<{ result: any[]; isProduction: boolean }>(
          `v2/api/companies/${companyId}/lenders/${providerType}`
        ),
      "Lenders fetched successfully",
      "An unexpected error occurred while fetching lenders"
    ).then(({ success, data }: any) => {
      if (success && data?.result?.length > 0) {
        const formatedData = data.result.map((d: any) => formateLender(d));
        dispatch(
          setLenderSettings({
            lenders: formatedData,
            isProduction: data.isProduction,
          })
        );
      }
      return data;
    });
  };
}

export function addLender(companyId: string, payload: any) {
  return async () => {
    return handleApiCall(
      () =>
        authApi.post<{ type: string; message?: string }>(
          `v2/api/companies/${companyId}/lender`,
          payload
        ),
      "Lender added successfully",
      "An unexpected error occurred while adding the lender"
    ).then(({ success }: any) => {
      if (success) {
        dispatch(fetchLenderAccount(companyId, payload.provider_type));
        dispatch(getLenderAccountList(companyId, payload.provider_type));
      }
    });
  };
}

export function editLender(companyId: string, payload: any) {
  return async () => {
    return handleApiCall(
      () =>
        authApi.post<{ type: string; message?: string }>(
          `v2/api/companies/${companyId}/lender/edit/${payload._id}`,
          payload
        ),
      "Lender updated successfully",
      "An unexpected error occurred while updating the lender"
    ).then(({ success }: any) => {
      if (success) {
        dispatch(getLenderAccountList(companyId, payload.provider_type));
      }
    });
  };
}

export function changeLenderStatus(
  companyId: string,
  payload: { id: string; status: number; providerType: string }
) {
  return async () => {
    return handleApiCall(
      () =>
        authApi.put<{ type: string; message?: string }>(
          `v2/api/companies/lender/${payload.id}/status`,
          payload
        ),
      "Lender status updated successfully",
      "An unexpected error occurred while updating lender status"
    ).then(({ success }: any) => {
      if (success) {
        dispatch(getLenderAccountList(companyId, payload.providerType));
      }
    });
  };
}

export function fetchLenderAccount(companyId: string, providerType: string) {
  return async () => {
    dispatch(setLoadingStatus(true));
    return handleApiCall(
      () =>
        authApi.get<{ type: string; data: any }>(
          `v2/api/companies/${companyId}/lender/account/${providerType}`
        ),
      "Lender account fetched successfully",
      "An unexpected error occurred while fetching lender account"
    ).then(({ success, data }: any) => {
      dispatch(setLoadingStatus(false));
      if (success && data?.type === "success") {
        dispatch(setLenderAccount(data.data));
      }
    });
  };
}
